import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { loanService } from '../../services/loanService';
import { groupService } from '../../services/groupService';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const GROUP_LOAN_MAX = 500000;

const LoanRequest = () => {
  const { user, effectiveRole } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [groups, setGroups] = useState<Array<{ id: string; name: string; currency: string }>>([]);
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string>('VT');
  const [loanSettings, setLoanSettings] = useState<any>(null);
  const [memberTotalContributions, setMemberTotalContributions] = useState<number>(0);
  const [availableCashInHand, setAvailableCashInHand] = useState<number>(0);
  const [lendingEnabled, setLendingEnabled] = useState(true);

  const [canRequestForMember, setCanRequestForMember] = useState(false);
  const [groupMembers, setGroupMembers] = useState<
    Array<{ user_id: string; full_name: string; email?: string }>
  >([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');

  const [formData, setFormData] = useState({
    amount: '',
    purpose: 'emergency',
    additionalDetails: '',
    repaymentMonths: '3',
    agreedToTerms: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (!user) return;

      try {
        const userGroups = await groupService.getUserGroups(user.id);
        setGroups(userGroups);
        if (userGroups.length > 0) {
          setCurrentGroupId(userGroups[0].id);
          setCurrency(userGroups[0].currency);
        }
      } catch (e) {
        console.error('Failed to load groups:', e);
      } finally {
        setPageLoading(false);
      }
    };

    init();
  }, [user, navigate]);

  useEffect(() => {
    const loadContext = async () => {
      if (!user || !currentGroupId) return;

      try {
        const onBehalf = await groupService.canRequestLoanOnBehalf(currentGroupId, user.id);
        setCanRequestForMember(onBehalf);

        const lending = await groupService.isLendingEnabled(currentGroupId);
        setLendingEnabled(lending);

        if (onBehalf) {
          const members = await groupService.getGroupMembersWithProfiles(currentGroupId);
          setGroupMembers(members.filter((m) => m.user_id !== user.id));
          setSelectedMemberId('');
        } else {
          setGroupMembers([]);
          setSelectedMemberId(user.id);
        }

        const settings = await loanService.getLoanSettings(currentGroupId);
        setLoanSettings(settings);

        const group = groups.find((g) => g.id === currentGroupId);
        if (group) setCurrency(group.currency);

        const cash = await loanService.getGroupCashInHand(currentGroupId);
        setAvailableCashInHand(cash);
      } catch (e) {
        console.error('Failed to load loan context:', e);
      }
    };

    loadContext();
  }, [user, currentGroupId, groups]);

  useEffect(() => {
    const loadMemberEligibility = async () => {
      if (!currentGroupId) return;

      const memberId = canRequestForMember ? selectedMemberId : user?.id;
      if (!memberId) {
        setMemberTotalContributions(0);
        return;
      }

      try {
        const contrib = await loanService.getMemberTotalContributions(memberId, currentGroupId);
        setMemberTotalContributions(contrib);
      } catch (e) {
        console.error('Failed to load member contributions:', e);
      }
    };

    loadMemberEligibility();
  }, [currentGroupId, selectedMemberId, canRequestForMember, user]);

  const targetMemberId = canRequestForMember ? selectedMemberId : user?.id;
  const minContribution = Number(loanSettings?.min_contribution_amount ?? 0);
  const isEligible = memberTotalContributions >= minContribution;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!lendingEnabled) {
      newErrors.general = 'Lending is disabled for this group';
    }

    if (canRequestForMember && !selectedMemberId) {
      newErrors.member = 'Please select a member';
    }

    const amount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amount) || amount <= 0) {
      newErrors.amount = 'Please enter a valid loan amount';
    } else if (amount > GROUP_LOAN_MAX) {
      newErrors.amount = `Maximum loan amount is ${GROUP_LOAN_MAX.toLocaleString()} ${currency}`;
    } else if (
      (loanSettings?.default_loan_type || 'group_total_contribution') === 'group_total_contribution' &&
      amount > availableCashInHand
    ) {
      newErrors.amount = `Amount exceeds available group funds (${availableCashInHand.toLocaleString()} ${currency})`;
    }

    if (!isEligible) {
      newErrors.general = `Member must have at least ${minContribution.toLocaleString()} ${currency} in contributions`;
    }

    if (!formData.purpose.trim()) {
      newErrors.purpose = 'Please select a purpose for the loan';
    }

    const months = parseInt(formData.repaymentMonths, 10);
    if (!formData.repaymentMonths || isNaN(months) || months <= 0) {
      newErrors.repaymentMonths = 'Please select a repayment period';
    }

    if (loanSettings?.max_repayment_months && months > Number(loanSettings.max_repayment_months)) {
      newErrors.repaymentMonths = `Maximum repayment period is ${loanSettings.max_repayment_months} months`;
    }

    if (!formData.agreedToTerms) {
      newErrors.terms = 'You must agree to the loan terms';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const interestRate = Number(loanSettings?.interest_rate ?? 10);
  const repaymentMonths = Math.max(1, parseInt(formData.repaymentMonths, 10) || 3);
  const requestedAmount = Math.max(0, parseFloat(formData.amount || '0') || 0);
  const totalRepaymentAmount = requestedAmount + requestedAmount * (interestRate / 100);
  const monthlyPaymentAmount = repaymentMonths > 0 ? totalRepaymentAmount / repaymentMonths : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !user || !currentGroupId || !targetMemberId) return;

    setLoading(true);

    try {
      const result = await loanService.submitLoanRequest({
        groupId: currentGroupId,
        memberId: targetMemberId,
        requestedAmount,
        purpose: formData.purpose,
        purposeNotes: formData.additionalDetails,
        repaymentMonths,
        interestRate,
        totalRepaymentAmount,
        monthlyPaymentAmount,
        memberTotalContributions,
        loanType: (loanSettings?.default_loan_type as string) || 'group_total_contribution',
        requestedBy: canRequestForMember && targetMemberId !== user?.id ? user?.id : undefined,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to submit loan request');
      }

      setSubmitSuccess(true);
      setFormData({
        amount: '',
        purpose: 'emergency',
        additionalDetails: '',
        repaymentMonths: '3',
        agreedToTerms: false,
      });

      setTimeout(() => {
        navigate(canRequestForMember ? '/loan-management' : '/member-dashboard');
      }, 2000);
    } catch (error: any) {
      console.error('Error submitting loan request:', error);
      setErrors({ general: error.message || 'Failed to submit loan request. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const pageTitle = canRequestForMember ? 'Request Loan for Member' : 'Request Loan';

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle} - communitySaving</title>
        <meta name="description" content="Request a loan from your community savings group" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header
          onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isMobileMenuOpen={isMobileMenuOpen}
        />

        <div className="flex">
          <div className="hidden lg:block">
            <Sidebar isCollapsed={isSidebarCollapsed} userRole={effectiveRole} />
          </div>

          <main className={`flex-1 transition-layout ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
            <div className="p-4 lg:p-6 max-w-4xl mx-auto">
              <div className="mb-6">
                <BreadcrumbTrail />
              </div>

              <div className="mb-6">
                <h1 className="text-2xl lg:text-3xl font-semibold text-foreground mb-2">
                  {pageTitle}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {canRequestForMember
                    ? 'Submit a loan request on behalf of a group member'
                    : 'Submit a loan request to your group administrators'}
                </p>
              </div>

              {groups.length === 0 ? (
                <div className="bg-card border border-border rounded-md p-8 text-center">
                  <p className="text-muted-foreground">Join a group before requesting a loan.</p>
                </div>
              ) : (
                <>
                  {submitSuccess && (
                    <div className="mb-6 bg-success/10 border border-success rounded-md p-4">
                      <div className="flex items-center gap-3">
                        <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                        <div>
                          <h3 className="text-sm font-semibold text-foreground">
                            Loan Request Submitted
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            The loan request is pending approval.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {!lendingEnabled && (
                    <div className="mb-6 bg-warning/10 border border-warning rounded-md p-4">
                      <p className="text-sm text-foreground">
                        Lending is disabled for this group. Contact your group admin.
                      </p>
                    </div>
                  )}

                  <div className="bg-card border border-border rounded-md p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {errors.general && (
                        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                          <p className="text-sm text-destructive">{errors.general}</p>
                        </div>
                      )}

                      {groups.length > 1 && (
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Group
                          </label>
                          <select
                            value={currentGroupId || ''}
                            onChange={(e) => {
                              const id = e.target.value;
                              setCurrentGroupId(id);
                              const g = groups.find((gr) => gr.id === id);
                              if (g) setCurrency(g.currency);
                            }}
                            disabled={loading}
                            className="w-full px-4 py-3 rounded-md border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          >
                            {groups.map((g) => (
                              <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {canRequestForMember && (
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Member
                          </label>
                          <select
                            value={selectedMemberId}
                            onChange={(e) => {
                              setSelectedMemberId(e.target.value);
                              setErrors((prev) => ({ ...prev, member: '' }));
                            }}
                            disabled={loading}
                            className="w-full px-4 py-3 rounded-md border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          >
                            <option value="">Select a member</option>
                            {groupMembers.map((m) => (
                              <option key={m.user_id} value={m.user_id}>
                                {m.full_name}
                              </option>
                            ))}
                          </select>
                          {errors.member && (
                            <p className="mt-1 text-sm text-destructive">{errors.member}</p>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 bg-muted/30 rounded-md">
                        <div>
                          <div className="text-xs text-muted-foreground">Member contributions</div>
                          <div className="text-sm font-semibold text-foreground">
                            {memberTotalContributions.toLocaleString()} {currency}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Min required</div>
                          <div className="text-sm font-semibold text-foreground">
                            {minContribution.toLocaleString()} {currency}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Available for loans</div>
                          <div className="text-sm font-semibold text-foreground">
                            {availableCashInHand.toLocaleString()} {currency}
                          </div>
                        </div>
                      </div>

                      <Input
                        type="number"
                        label="Loan Amount"
                        placeholder="Enter loan amount"
                        value={formData.amount}
                        onChange={(e) => {
                          setFormData((prev) => ({ ...prev, amount: e.target.value }));
                          setErrors((prev) => ({ ...prev, amount: '' }));
                        }}
                        error={errors.amount}
                        required
                        disabled={loading || !lendingEnabled}
                        min="0"
                        step="0.01"
                      />

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Purpose of Loan
                        </label>
                        <select
                          value={formData.purpose}
                          onChange={(e) => {
                            setFormData((prev) => ({ ...prev, purpose: e.target.value }));
                            setErrors((prev) => ({ ...prev, purpose: '' }));
                          }}
                          disabled={loading || !lendingEnabled}
                          className="w-full px-4 py-3 rounded-md border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          <option value="emergency">Emergency</option>
                          <option value="business">Business</option>
                          <option value="education">Education</option>
                          <option value="health">Health</option>
                          <option value="housing">Housing</option>
                          <option value="other">Other</option>
                        </select>
                        {errors.purpose && (
                          <p className="mt-1 text-sm text-destructive">{errors.purpose}</p>
                        )}
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Additional Details (optional)
                          </label>
                          <textarea
                            value={formData.additionalDetails}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, additionalDetails: e.target.value }))
                            }
                            placeholder="Provide more details about the loan purpose..."
                            disabled={loading || !lendingEnabled}
                            rows={3}
                            maxLength={500}
                            className="w-full px-4 py-3 rounded-md border border-input bg-card text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Repayment Period (months)
                        </label>
                        <select
                          value={formData.repaymentMonths}
                          onChange={(e) => {
                            setFormData((prev) => ({ ...prev, repaymentMonths: e.target.value }));
                            setErrors((prev) => ({ ...prev, repaymentMonths: '' }));
                          }}
                          disabled={loading || !lendingEnabled}
                          className="w-full px-4 py-3 rounded-md border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          {Array.from(
                            { length: Number(loanSettings?.max_repayment_months ?? 12) },
                            (_, i) => i + 1
                          ).map((m) => (
                            <option key={m} value={String(m)}>{m}</option>
                          ))}
                        </select>
                        {errors.repaymentMonths && (
                          <p className="mt-1 text-sm text-destructive">{errors.repaymentMonths}</p>
                        )}
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="p-3 bg-muted/30 rounded-md">
                            <div className="text-xs text-muted-foreground">Interest rate</div>
                            <div className="text-sm font-semibold text-foreground">
                              {interestRate.toFixed(1)}%
                            </div>
                          </div>
                          <div className="p-3 bg-muted/30 rounded-md">
                            <div className="text-xs text-muted-foreground">Total repayment</div>
                            <div className="text-sm font-semibold text-foreground">
                              {totalRepaymentAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })} {currency}
                            </div>
                          </div>
                          <div className="p-3 bg-muted/30 rounded-md">
                            <div className="text-xs text-muted-foreground">Monthly payment</div>
                            <div className="text-sm font-semibold text-foreground">
                              {monthlyPaymentAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })} {currency}
                            </div>
                          </div>
                        </div>
                      </div>

                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.agreedToTerms}
                          onChange={(e) => {
                            setFormData((prev) => ({ ...prev, agreedToTerms: e.target.checked }));
                            setErrors((prev) => ({ ...prev, terms: '' }));
                          }}
                          disabled={loading || !lendingEnabled}
                          className="mt-1"
                        />
                        <span className="text-sm text-muted-foreground">
                          I understand this loan must be repaid according to the group schedule and
                          terms set by the group admin.
                        </span>
                      </label>
                      {errors.terms && (
                        <p className="text-sm text-destructive">{errors.terms}</p>
                      )}

                      <div className="flex gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => navigate(effectiveRole === 'admin' ? '/loan-management' : '/member-dashboard')}
                          disabled={loading}
                          fullWidth
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="default"
                          disabled={loading || !lendingEnabled}
                          loading={loading}
                          iconName="Send"
                          iconPosition="right"
                          fullWidth
                        >
                          Submit Request
                        </Button>
                      </div>
                    </form>
                  </div>
                </>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default LoanRequest;
